/**
 * Agent Selector Component
 * 
 * Sidebar navigation for the agent workspace.
 * Displays available agents in a list with icons.
 * Uses AgentHubService to get the same agents as the hub.
 */
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AgentHubService } from '../../../services/agent-hub/agent-hub.service';
import { LoggerService } from '../../../../../services/logger-services/logger.service';
import { AgentHubItem } from '../../../models';
import { RegulationsHubRegRadarService } from '../../../services/regulations-hub-reg-radar/regulations-hub-reg-radar.service';
import { AI_WORKBENCH_AGENT_ID } from '../../../services/agent-orchestrator/agent-orchestrator.service';

@Component({
  selector: 'app-workspace-agent-selector',
  templateUrl: './agent-selector.component.html',
  styleUrls: ['./agent-selector.component.scss']
})
export class AgentSelectorComponent implements OnInit, OnDestroy {

  /** Currently selected agent ID */
  @Input() currentAgentId: string = '';

  /** Whether we're in agent hub mode */
  @Input() isAgentHubMode: boolean = true;

  /** Tenant workbench feature flag ∩ user privilege*/
  @Input() workbenchAccessAllowed = false;

  /** Optional chatbot ref for RegRadar (regulations agent) — same as agent hub cards. */
  @Input() chatbotComponentRef: any = null;

  /** Emitted when sidebar toggle is requested */
  @Output() sidebarToggled = new EventEmitter<void>();

  /** Emitted when an agent is selected */
  @Output() agentSelected = new EventEmitter<string>();

  /** Emitted when user wants to navigate to hub (All Agents) */
  @Output() navigateToHub = new EventEmitter<void>();

  /** Emitted when user opens AI Workbench (iframe) */
  @Output() navigateToAiWorkbench = new EventEmitter<void>();

  /** Cleanup subject */
  private readonly destroy$ = new Subject<void>();

  /** List of available agents from AgentHubService */
  agents: AgentHubItem[] = [];

  /** Loading state */
  isLoading: boolean = true;

  constructor(
    private readonly agentHubService: AgentHubService,
    private readonly logger: LoggerService,
    private readonly regulationsHubRegRadarService: RegulationsHubRegRadarService
  ) {}

  ngOnInit(): void {
    this.logger.logInfo('[AgentSelector] Initializing', 'ngOnInit');
    this.loadAgents();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load available agents using AgentHubService (same as agent-hub)
   */
  private loadAgents(): void {
    this.isLoading = true;
    this.agentHubService.getAllAgents()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
          this.isLoading = false;
          this.logger.logInfo(`[AgentSelector] Loaded ${agents.length} agents`, 'loadAgents');
        },
        error: (err) => {
          this.logger.logInfo('[AgentSelector] Failed to load agents', 'loadAgents', err);
          this.isLoading = false;
        }
      });
  }

  /**
   * Handle collapse menu click
   */
  onCollapseMenu(): void {
    this.sidebarToggled.emit();
  }

  /**
   * Handle agent selection (RegRadar for regulations agent matches {@link AgentCardComponent}).
   */
  onAgentClick(agent: AgentHubItem): void {
    if (agent?.id === 'regulations_agent') {
      this.regulationsHubRegRadarService.loadHubRegRadarSliceForRegulationsAgent(this.chatbotComponentRef);
    }
    this.agentSelected.emit(agent.id);
  }

  /**
   * Keyboard activation for list items (prevent Space from scrolling the page).
   */
  onAgentItemKeydown(event: KeyboardEvent, agent: AgentHubItem): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onAgentClick(agent);
    }
  }

  /**
   * Handle "All Agents" click - navigate to agent hub
   */
  onAllAgentsClick(): void {
    this.navigateToHub.emit();
  }

  /**
   * Open AI Workbench (embedded URL from config).
   */
  onAiWorkbenchClick(): void {
    this.navigateToAiWorkbench.emit();
  }

  /**
   * Check if agent is currently selected
   */
  isAgentActive(agent: AgentHubItem): boolean {
    return this.currentAgentId === agent.id;
  }

  /**
   * Check if "All Agents" is active (hub mode)
   */
  isAllAgentsActive(): boolean {
    return this.isAgentHubMode;
  }

  /** AI Workbench nav item is selected */
  isAiWorkbenchActive(): boolean {
    return this.currentAgentId === AI_WORKBENCH_AGENT_ID;
  }

  /**
   * Get agent icon source
   */
  getAgentIcon(agent: AgentHubItem): string {
    if (agent.icon) {
      return agent.icon.startsWith('http') || agent.icon.startsWith('assets')
        ? agent.icon
        : `assets/images/agents/${agent.icon}`;
    }
    return 'assets/images/agents/default-agent.svg';
  }
}
